import React from 'react'
import {
  Artboard,
  Page,
  useArtboard,
  useCurrentArtboard,
  useNavigation,
  usePages,
} from '@sketch-hq/sketch-prototype-player'
import { RouteParams } from '@sketch/modules-common'
import { useVersioning } from 'modules/versioning'
import { useParams } from 'react-router-dom'

import { Dropdown, useResponsiveDropdown } from '@sketch/components'
import NavigationItem from 'modules/shares/components/HeaderNavigation/NavigationItem'
import {
  ChevronsIcon,
  DropdownItem,
} from 'modules/shares/components/HeaderNavigation/NavigationItem.styles'
import {
  ArtboardIcon,
  ArtboardSelectorButton,
  StartPointIcon,
} from './PrototypeArtboardsNavigationItem.styles'
import {
  usePrototypeStartArtboardId,
  usePrototypeViewContext,
} from 'modules/prototype/PrototypeView/hooks'
import { CurrentPrototype } from 'modules/prototype/PrototypeView/hooks/usePrototypeViewContext/types'

const DROPDOWN_STYLE = {
  maxHeight: '80vh',
  overflow: 'auto',
} as const

/**
 * @TODO Remove this component with `better-prototype-nav-bar` FF clean up.
 */
export function PrototypeArtboardsNavigationItem() {
  const { artboardUUID } = useParams<RouteParams<'PROTOTYPE_VIEW'>>()

  const prototypeStartArtboardId = usePrototypeStartArtboardId()
  const prototypeStartArtboard = useArtboard(prototypeStartArtboardId)

  const currentArtboard = useCurrentArtboard()
  const { currentPrototype } = usePrototypeViewContext()

  const prototypeName =
    currentPrototype.type === 'orphan_prototype'
      ? currentArtboard?.name
      : prototypeStartArtboard?.name

  const { restartPrototypeFrom } = useNavigation()
  const pages = usePages()
  const artboards = pages.flatMap(page => page.artboards)

  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: ArtboardsDropdown,
    dropdownProps: {
      pages,
      currentArtboardId: artboardUUID,
      currentPrototype,
      restartPrototypeFrom,
    },
    dropdownStyle: DROPDOWN_STYLE,
  })

  const artboardCount = artboards.length || 0

  const iconId =
    currentPrototype.type === 'orphan_prototype' ? 'artboard' : 'prototype'

  if (artboardCount === 1) {
    return <NavigationItem icon={iconId} label={artboards[0].name || ''} />
  }

  return (
    <>
      <ArtboardSelectorButton
        data-testid="prototype-artboard-selector"
        {...buttonProps}
      >
        <NavigationItem label={prototypeName} icon={iconId} />
        <ChevronsIcon />
      </ArtboardSelectorButton>
      {content}
    </>
  )
}

interface ArtboardsDropdownProps {
  pages: Page[]
  currentArtboardId: string
  currentPrototype: CurrentPrototype
  restartPrototypeFrom: (artboardId: string) => void
}

function ArtboardsDropdown({
  pages,
  currentArtboardId,
  currentPrototype,
  restartPrototypeFrom,
}: ArtboardsDropdownProps) {
  const { getPathname, share } = useVersioning()
  const getArtboardLink = (artboard: Artboard) =>
    getPathname({
      routeKey: 'PROTOTYPE_VIEW',
      routeParams: {
        shareID: share.identifier,
        artboardUUID: artboard.id,
        query: {
          prototype: artboard.id,
        },
      },
    })

  return (
    <>
      {pages.map(page => {
        const isCurrentArtboardOfOrphanPrototype = (artboard: Artboard) => {
          return (
            currentPrototype.type === 'orphan_prototype' &&
            artboard.id === currentArtboardId
          )
        }
        const artboardsToDisplay = page.artboards
          .filter(
            artboard =>
              artboard.isFlowHome === true ||
              // We would normally only show artboards that are start points however,
              // in the legacy scenario of orphan prototypes we show the current artboard
              // as a fallback even if it's not an entry point.
              isCurrentArtboardOfOrphanPrototype(artboard)
          )
          .reverse()

        if (!artboardsToDisplay.length) {
          return null
        }

        return (
          <div key={page.id}>
            <Dropdown.Header>{page.name}</Dropdown.Header>
            {artboardsToDisplay.map(artboard => (
              <DropdownItem
                key={artboard.id}
                isActive={() => {
                  return currentPrototype.type === 'orphan_prototype'
                    ? artboard.id === currentArtboardId
                    : artboard.id === currentPrototype.startPointArtboardId
                }}
                to={getArtboardLink(artboard)}
                disabled={!artboard.isFlowHome}
                onClick={() => restartPrototypeFrom(artboard.id)}
              >
                {artboard.isFlowHome ? <StartPointIcon /> : <ArtboardIcon />}
                {artboard.name}
              </DropdownItem>
            ))}
          </div>
        )
      })}
    </>
  )
}
